import { makeVar, useReactiveVar } from "@apollo/client";

import { DeviceData, Service } from "../../../API";
import { AutocompleteOptionType } from "../../../common/models/autocomplete";
import { IFormValidationState } from "../../../common/models/formValidation";
import { IDeviceTableRow } from "../hooks/useDeviceTableRows";

export interface DisplayNameInput {
  id: string;
  name: string;
}

interface IDeviceConfig extends DeviceData {
  [key: string]: any;
}

export type TDeviceConfig = Omit<IDeviceConfig, "__typename">;

interface IDeviceForm {
  isDirty?: boolean;
  deviceName: string;
  location: AutocompleteOptionType;
  node: DisplayNameInput | null;
  nodeIP?: string | null;
  deviceType: string;
  cameraIpAddress: string;
  deviceData: TDeviceConfig;
  deviceTypeShortName?: string;
  hasPanTilt?: string;
  hasZoom?: boolean;
  rtspTemplate?: string;
  defaultUserPwd?: string;
  deviceMakePanTiltValue?: boolean;
  deviceMakeHasZoomValue?: boolean;
  id: string;
}

interface IDeviceFormValidationState {
  location: IFormValidationState;
  deviceName: IFormValidationState;
  node: IFormValidationState;
  deviceType: IFormValidationState;
  cameraIpAddress: IFormValidationState;
  panTiltIP: IFormValidationState;
  sourceVideo: IFormValidationState;
  [key: string]: IFormValidationState;
}

export const defaultDeviceFormValidationState: IDeviceFormValidationState = {
  location: {
    hasError: false,
    errorMessage: "",
  },
  deviceName: {
    hasError: false,
    errorMessage: "",
  },
  node: {
    hasError: false,
    errorMessage: "",
  },
  deviceType: {
    hasError: false,
    errorMessage: "",
  },
  cameraIpAddress: {
    hasError: false,
    errorMessage: "",
  },
  panTiltIP: {
    hasError: false,
    errorMessage: "",
  },
  sourceVideo: {
    hasError: false,
    errorMessage: "",
  },
};

export const deviceFormRules = {
  deviceName: {
    required: true,
    unique: true,
    pattern: /^[a-zA-Z0-9_.-]+$/, // no spaces, only alphanumerics, underscore, dash and dot
    maxLength: 30,
  },
  node: {
    required: true,
  },
  deviceType: {
    required: true,
  },
  cameraIpAddress: {
    required: true,
    pattern: /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/,
  },
  panTiltIP: {
    required: true,
    pattern: /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/,
  },
  sourceVideo: {
    pattern: /^[A-Za-z0-9._~!$&'()*+,;=:@/?%#[\]-]+$/,
  },
};

export const defaultDeviceFormValues: IDeviceForm = {
  isDirty: false,
  deviceName: "",
  location: {
    value: "",
    title: "",
  },
  node: {
    id: "",
    name: "",
  },
  nodeIP: "",
  deviceType: "",
  deviceData: {
    hasPanTilt: false,
    panTiltIP: "",
    recentFrame: "",
    keyFrame: "",
    rtspHost: "",
    sourceVideo: "",
    hasZoom: false,
    protocol: "",
    hasRecordVideo: false,
  },
  cameraIpAddress: "",
  deviceTypeShortName: "",
  rtspTemplate: "",
  defaultUserPwd: "",
  id: "",
};

export const deviceFormVariables = makeVar<IDeviceForm>(
  defaultDeviceFormValues
);

export const useDeviceFormVariables = () => useReactiveVar(deviceFormVariables);

export const selectedLocationDevicesVariable =
  makeVar<AutocompleteOptionType>(null);

export const useSelectedLocationDevices = () =>
  useReactiveVar(selectedLocationDevicesVariable);

export const devicesLoadingVariable = makeVar<boolean>(true);

export const useDevicesTableLoading = () =>
  useReactiveVar(devicesLoadingVariable);

export const deviceFormValidationStateVariable =
  makeVar<IDeviceFormValidationState>(defaultDeviceFormValidationState);

export const useDeviceFormValidationState = () =>
  useReactiveVar(deviceFormValidationStateVariable);

export const deviceDataVariable = makeVar<IDeviceTableRow | null>(null);

export const useDeviceDataState = () => useReactiveVar(deviceDataVariable);

export const servicesByLocationVariable = makeVar<Service[]>([]);

export const useServicesByLocationVariable = () =>
  useReactiveVar(servicesByLocationVariable);
